<template>
  <div class="container mt-5">
    <div class="card p-4 shadow border-0" style="border-radius: 16px;">
      <h4 class="mb-4 fw-bold" style="color: #b03a45;">➖ {{ $t('expenseAdd.title') }}</h4>

      <form @submit.prevent="handleSubmit">
        <div class="row">
          <!-- Type -->
          <div class="col-md-6 mb-3">
            <div class="bg-white p-3 rounded shadow-sm">
              <label class="form-label">{{ $t('expenseAdd.form.type') }}</label>
              <select v-model="form.type" class="form-select border-0" required>
                <option disabled value="">{{ $t('expenseAdd.form.selectType') }}</option>
                <option value="cash">{{ $t('expenseAdd.form.cash') }}</option>
                <option value="gift">{{ $t('expenseAdd.form.gift') }}</option>
                <option value="transfer">{{ $t('expenseAdd.form.transfer') }}</option>
              </select>
            </div>
          </div>

          <!-- Amount -->
          <div class="col-md-6 mb-3">
            <div class="bg-white p-3 rounded shadow-sm">
              <label class="form-label">{{ $t('expenseAdd.form.amount') }}</label>
              <input type="number" class="form-control border-0" v-model="form.amount" step="0.01" required />
            </div>
          </div>

          <!-- Date -->
          <div class="col-md-6 mb-3">
            <div class="bg-white p-3 rounded shadow-sm">
              <label class="form-label">{{ $t('expenseAdd.form.date') }}</label>
              <input type="date" class="form-control border-0" v-model="form.date" required />
            </div>
          </div>

          <!-- Note -->
          <div class="col-md-6 mb-3">
            <div class="bg-white p-3 rounded shadow-sm">
              <label class="form-label">{{ $t('expenseAdd.form.note') }}</label>
              <input type="text" class="form-control border-0" v-model="form.note" />
            </div>
          </div>

          <!-- Category -->
          <div class="col-md-6 mb-3">
            <div class="bg-white p-3 rounded shadow-sm">
              <label class="form-label">{{ $t('expenseAdd.form.category') }}</label>
              <select v-model="form.category_id" class="form-select border-0" required>
                <option disabled value="">{{ $t('expenseAdd.form.selectCategory') }}</option>
                <option v-for="cat in categories" :key="cat.id" :value="cat.id">
                  {{ cat.category_name }}
                </option>
              </select>
            </div>
          </div>

          <!-- Company -->
          <div class="col-md-6 mb-3">
            <div class="bg-white p-3 rounded shadow-sm">
              <label class="form-label">{{ $t('expenseAdd.form.company') }}</label>
              <select v-model="form.company_id" class="form-select border-0" required>
                <option disabled value="">{{ $t('expenseAdd.form.selectCompany') }}</option>
                <option v-for="company in companies" :key="company.id" :value="company.id">
                  {{ company.english_name }}
                </option>
              </select>
              <small v-if="usersLoading" class="text-muted">{{ $t('expenseAdd.form.loadingUsers') }}</small>
            </div>
          </div>

          <!-- Employer -->
          <div class="col-md-6 mb-3">
            <div class="bg-white p-3 rounded shadow-sm">
              <label class="form-label">{{ $t('expenseAdd.form.employer') }}</label>
              <select
                v-model="form.user_id"
                class="form-select border-0"
                :disabled="!form.company_id || usersLoading || users.length === 0"
                required
              >
                <option disabled value="">
                  {{
                    !form.company_id
                      ? $t('expenseAdd.form.selectCompanyFirst')
                      : (usersLoading ? $t('expenseAdd.form.loading') : $t('expenseAdd.form.selectEmployer'))
                  }}
                </option>
                <option v-for="user in users" :key="user.id" :value="user.id">
                  {{ user.firstname }} {{ user.lastname }}
                </option>
              </select>
              <small v-if="form.company_id && !usersLoading && users.length === 0" class="text-muted">
                {{ $t('expenseAdd.form.noUsers') }}
              </small>
            </div>
          </div>
        </div>

        <!-- Submit -->
        <div class="text-end mt-4">
          <button type="submit" class="btn text-white px-4" style="background-color: #b03a45;">
            {{ $t('expenseAdd.form.submit') }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useToast } from 'vue-toastification'
import { useI18n } from 'vue-i18n'
import axios from '@/api'

const { t } = useI18n()
const router = useRouter()
const toast = useToast()

const form = ref({
  type: '',
  amount: '',
  date: '',
  note: '',
  category_id: '',
  user_id: '',
  company_id: ''
})

const users = ref([])
const usersLoading = ref(false)
const companies = ref([])
const categories = ref([])

const fetchDependencies = async () => {
  try {
    const [companiesRes, categoriesRes] = await Promise.all([
      axios.get('/companies'),
      axios.get('/Financial/expense-categories')
    ])
    companies.value = companiesRes.data?.data || companiesRes.data || []
    categories.value = categoriesRes.data || []

    if (form.value.company_id) {
      fetchUsersByCompany(form.value.company_id)
    }
  } catch (err) {
    console.error(err)
    toast.error(t('expenseAdd.messages.loadError'))
  }
}

const fetchUsersByCompany = async (companyId) => {
  if (!companyId) {
    users.value = []
    return
  }
  usersLoading.value = true
  users.value = []
  try {
    const res = await axios.get(`/Financial/companies/${companyId}/users`)
    users.value = Array.isArray(res.data) ? res.data : (res.data?.data || [])
  } catch (err) {
    console.error(err)
    toast.error(t('expenseAdd.messages.usersError'))
  } finally {
    usersLoading.value = false
  }
}

watch(
  () => form.value.company_id,
  (newCompanyId) => {
    form.value.user_id = ''
    fetchUsersByCompany(newCompanyId)
  }
)

const handleSubmit = async () => {
  try {
    await axios.post('/Financial/expenses', form.value)
    toast.success(t('expenseAdd.messages.success'))
    router.push('/office-expenses')
  } catch (err) {
    console.error(err)
    toast.error(err.response?.data?.message || t('expenseAdd.messages.error'))
  }
}

onMounted(fetchDependencies)
</script>

<style scoped>
.form-label {
  font-weight: bold;
  color: #b03a45;
}
</style>
